import { Maximize2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui'
import { useChatMutations } from '@/modules/chat/hooks'
import { useAsyncError } from '@/modules/errors'
import { cn } from '@/utils'
import {
  ConfirmationFooter,
  useConfirmationCardState,
} from './confirmationCard'
import ExpandedArtifactDialog from './ExpandedArtifactDialog'

// Keys are camelCased to match the row shape; the wire-format snake_case
// is converted in artifactKinds.ts so this artifact uses one convention.
// Labels mirror the transaction table headers so the card never shows a
// raw field name like "accountOwner".
const EDITABLE_FIELD_LABELS: Record<string, string> = {
  merchant: 'Merchant',
  payee: 'Payee',
  description: 'Description',
  amount: 'Amount',
  date: 'Date',
  category: 'Category',
  categoryDetail: 'Category Detail',
  paymentChannel: 'Payment Channel',
  accountNumber: 'Account Number',
  accountType: 'Account Type',
  shareNumber: 'Share Number',
  institution: 'Institution',
  accountOwner: 'Account Owner',
}

export interface BulkEditPreviewSampleRow {
  id: number
  date?: string | null
  description?: string | null
  merchant?: string | null
  payee?: string | null
  amount?: number | null
  category?: string | null
  categoryDetail?: string | null
  paymentChannel?: string | null
  accountNumber?: string | null
  accountType?: string | null
  shareNumber?: string | null
  institution?: string | null
  accountOwner?: string | null
}

export type BulkEditFieldUpdateValue = string | number | null

export interface BulkEditPreviewSpec {
  batchToken: string
  affectedCount: number
  fieldUpdates: Record<string, BulkEditFieldUpdateValue>
  sample: BulkEditPreviewSampleRow[]
  warnings?: string[]
  expiresAt: string
}

interface BulkEditPreviewArtifactProps {
  slug: string
  spec: BulkEditPreviewSpec
}

export default function BulkEditPreviewArtifact({
  slug,
  spec,
}: BulkEditPreviewArtifactProps) {
  const { sendMessage } = useChatMutations()
  const throwAsyncError = useAsyncError()
  const { state, disabled, setRequested, setCancelledLocally } =
    useConfirmationCardState({
      expiresAt: spec.expiresAt,
      applyToolName: 'apply_bulk_edit',
      tokenField: 'batch_token',
      token: spec.batchToken,
    })
  const [expanded, setExpanded] = useState(false)

  const fieldEntries = Object.entries(spec.fieldUpdates)

  function applyChanges() {
    setRequested(true)
    sendMessage(slug, 'Apply the changes.', {
      kind: 'bulk_edit',
      batchToken: spec.batchToken,
    }).catch((error: Error) => {
      setRequested(false)
      throwAsyncError(error)
    })
  }

  function cancelChanges() {
    setCancelledLocally(true)
  }

  const footerProps = {
    expiresAt: spec.expiresAt,
    state,
    disabled,
    appliedMessage: 'Applied.',
    expiredMessage: 'Expired. Ask the assistant to preview again.',
    confirmLabel: `Apply ${spec.affectedCount.toLocaleString()}`,
  }

  return (
    <figure className="my-1 w-full overflow-hidden rounded-xl border bg-card shadow-sm">
      <figcaption className="flex items-center justify-between gap-3 border-b px-4 py-2.5 text-sm font-medium text-foreground">
        <span className="truncate">
          {headlineFor(spec.affectedCount, fieldEntries)}
        </span>
        {spec.sample.length > 0 && (
          <Button
            type="button"
            theme="ghost"
            size="xs"
            isIcon
            className="shrink-0"
            onClick={() => setExpanded(true)}
            title="Expand"
            aria-label="Expand"
          >
            <Maximize2 className="size-3.5" aria-hidden />
          </Button>
        )}
      </figcaption>

      <FieldUpdatesList fieldEntries={fieldEntries} />

      {spec.sample.length > 0 && (
        <SampleTable sample={spec.sample} fieldEntries={fieldEntries} />
      )}

      {spec.warnings && spec.warnings.length > 0 && (
        <ul className="border-b bg-amber-50 px-4 py-2 text-[12px] text-amber-900">
          {spec.warnings.map((warning) => (
            <li key={warning} className="flex gap-2">
              <span aria-hidden>⚠</span>
              <span>{warning}</span>
            </li>
          ))}
        </ul>
      )}

      <ConfirmationFooter
        {...footerProps}
        onApply={applyChanges}
        onCancel={cancelChanges}
      />

      <ExpandedArtifactDialog
        open={expanded}
        onOpenChange={setExpanded}
        title={headlineFor(spec.affectedCount, fieldEntries)}
      >
        <FieldUpdatesList fieldEntries={fieldEntries} />
        <SampleTable
          sample={spec.sample}
          fieldEntries={fieldEntries}
          expanded
          className="max-h-none flex-1"
        />
        <ConfirmationFooter
          {...footerProps}
          onApply={() => {
            setExpanded(false)
            applyChanges()
          }}
          onCancel={() => {
            setExpanded(false)
            cancelChanges()
          }}
        />
      </ExpandedArtifactDialog>
    </figure>
  )
}

interface FieldUpdatesListProps {
  fieldEntries: Array<[string, BulkEditFieldUpdateValue]>
}

function FieldUpdatesList({ fieldEntries }: FieldUpdatesListProps) {
  return (
    <dl className="divide-y border-b">
      <div className="flex items-center justify-between gap-4 bg-foreground/[0.03] px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground/80">
        <span>Field</span>
        <span className="pr-2">New value</span>
      </div>
      {fieldEntries.map(([field, newValue]) => (
        <div
          key={field}
          className="flex items-center justify-between gap-4 px-4 py-2.5"
        >
          <dt className="text-[13px] font-medium text-foreground">
            {EDITABLE_FIELD_LABELS[field] ?? field}
          </dt>
          <dd className="min-w-0 truncate">
            <span className="rounded-md bg-emerald-50 px-2 py-1 text-[13px] font-semibold text-emerald-900 ring-1 ring-inset ring-emerald-200">
              {formatValue(newValue)}
            </span>
          </dd>
        </div>
      ))}
    </dl>
  )
}

interface SampleTableProps {
  sample: BulkEditPreviewSampleRow[]
  fieldEntries: Array<[string, BulkEditFieldUpdateValue]>
  expanded?: boolean
  className?: string
}

function SampleTable({
  sample,
  fieldEntries,
  expanded = false,
  className,
}: SampleTableProps) {
  const editedFields = fieldEntries.map(([field]) => field)
  const descriptionMaxWidth = expanded ? 'max-w-[720px]' : 'max-w-[320px]'
  return (
    <div
      className={cn(
        'max-h-64 overflow-auto scrollbar-thin scrollbar-thumb-neutral-300 hover:scrollbar-thumb-neutral-400 scrollbar-track-transparent',
        className,
      )}
    >
      <table className="w-full text-[13px]">
        <thead className="sticky top-0 z-10 border-b bg-card text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left">Date</th>
            <th className="px-3 py-2 text-left">Description</th>
            {editedFields.map((field) => (
              <th key={field} className="px-3 py-2 text-left">
                {EDITABLE_FIELD_LABELS[field] ?? field} (current)
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sample.map((row) => (
            <tr key={row.id} className="border-t">
              <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                {row.date ?? ''}
              </td>
              <td className="px-3 py-2">
                <div
                  className={cn('truncate', descriptionMaxWidth)}
                  title={row.description ?? ''}
                >
                  {row.description ?? ''}
                </div>
              </td>
              {editedFields.map((field) => (
                <td key={field} className="px-3 py-2 text-muted-foreground">
                  {currentValueFor(row, field)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function currentValueFor(row: BulkEditPreviewSampleRow, field: string): string {
  const value = row[field as keyof BulkEditPreviewSampleRow]
  return value == null ? '' : String(value)
}

function formatValue(value: BulkEditFieldUpdateValue): string {
  if (value === null) return '(cleared)'
  if (value === '') return '(empty)'
  return String(value)
}

function headlineFor(
  affectedCount: number,
  fieldEntries: Array<[string, BulkEditFieldUpdateValue]>,
): string {
  const fieldList = fieldEntries
    .map(([field]) => EDITABLE_FIELD_LABELS[field] ?? field)
    .join(', ')
  return `Update ${fieldList} for ${affectedCount.toLocaleString()} ${pluralize('transaction', affectedCount)}`
}

function pluralize(word: string, count: number): string {
  return count === 1 ? word : `${word}s`
}
